import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2 } from "lucide-react";

export default function StudentQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [answers, setAnswers] = useState({});

  const load = async () => {
    const [e, a, all] = await Promise.all([
      api.get("/enrollments/me"),
      api.get("/quizzes/me/attempts"),
      api.get("/quizzes"),
    ]);
    const courseIds = e.data.map((x) => x.course_id);
    setQuizzes(all.data.filter((q) => courseIds.includes(q.course_id)));
    setAttempts(a.data);
  };
  useEffect(() => { load(); }, []);

  const attempted = (id) => attempts.find((a) => a.quiz_id === id);

  const submit = async (quiz) => {
    const arr = quiz.questions.map((_, i) => answers[quiz.id]?.[i] ?? -1);
    try {
      const res = await api.post(`/quizzes/${quiz.id}/attempt`, arr);
      toast.success(`Scored ${res.data.score}/${res.data.total}`);
      load();
    } catch (err) { toast.error("Attempt failed"); }
  };

  return (
    <Layout title="Quizzes" subtitle="Student · Assessments">
      <div className="space-y-4">
        {quizzes.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm">
            No quizzes available yet.
          </div>
        )}
        {quizzes.map((q) => {
          const done = attempted(q.id);
          return (
            <div key={q.id} data-testid={`quiz-item-${q.id}`} className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-violet-600" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Quiz</span>
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900">{q.title}</h3>
              {done ? (
                <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-emerald-800 text-sm inline-flex items-center gap-2" data-testid={`quiz-result-${q.id}`}>
                  <CheckCircle2 className="w-4 h-4" /> You scored {done.score}/{done.total}
                </div>
              ) : (
                <div className="mt-3 space-y-4">
                  {q.questions.map((qq, qi) => (
                    <div key={qi} className="border border-slate-100 rounded-xl p-4">
                      <div className="text-sm font-medium text-slate-900 mb-2">{qi + 1}. {qq.q}</div>
                      <div className="space-y-1.5">
                        {qq.options.map((opt, oi) => (
                          <label key={oi} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                            <input
                              data-testid={`quiz-${q.id}-q${qi}-o${oi}`}
                              type="radio" name={`${q.id}-${qi}`}
                              checked={answers[q.id]?.[qi] === oi}
                              onChange={() => setAnswers((a) => ({ ...a, [q.id]: { ...(a[q.id] || {}), [qi]: oi } }))}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button data-testid={`submit-quiz-${q.id}`} onClick={() => submit(q)} className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700">
                    Submit quiz
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
